import React from 'react';

import { Button, Grid } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { DateFormatPatterns, STATUSES_OPTIONS } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { formatDate, formatPhoneNumber, parseNullableValue } from '@clearverify/utils';

import { useChangeUserStatusMutation } from 'src/api';
import { DetailGridItem, FullscreenLoader } from 'src/components/common';
import { SelectField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useConsumerById } from 'src/hooks/consumer';
import { useUser } from 'src/hooks/user';
import { isEnableStatus } from 'src/utils/user';

import {
  ConsumerManageAccountActivityFormField,
  ConsumerManageAccountActivityFormValues,
} from './types';

export interface ConsumerManageAccountActivityFormProps {
  onCloseModal: () => void;
  consumerId: Nullable<string>;
  switchClosability: (closability: boolean) => void;
  onUpdated?: () => void;
}

export const ConsumerManageAccountActivityForm: React.FC<
  ConsumerManageAccountActivityFormProps
> = ({ switchClosability, onCloseModal, consumerId, onUpdated }) => {
  const { createApolloContext } = useNotification();

  const { consumer, loading, updating } = useConsumerById(consumerId);
  const { user: employeeUser } = useUser(consumer?.userId);

  const shouldRenderStatusField = !consumer?.dataDeletedAt && isEnableStatus(employeeUser?.status);

  const [changeUserStatus, { loading: updatingUser }] = useChangeUserStatusMutation({
    context: createApolloContext({
      successMessage: 'Employee has been successfully updated',
    }),
  });

  const handleSubmit = React.useCallback(
    async (formValues: ConsumerManageAccountActivityFormValues) => {
      if (!employeeUser?.id || !formValues.userStatus) return;

      try {
        await changeUserStatus({
          variables: {
            userId: employeeUser.id,
            newStatus: formValues.userStatus,
          },
        });

        onUpdated?.();
        switchClosability(true);
        onCloseModal();
      } catch {
        switchClosability(true);
      }
    },
    [changeUserStatus, employeeUser?.id, onCloseModal, onUpdated, switchClosability],
  );

  const onClose = () => {
    onCloseModal();
  };

  const { firstName, lastName, phoneNumber, email } = employeeUser ?? {};

  const initialValues: ConsumerManageAccountActivityFormValues = {
    userStatus: employeeUser?.status,
  };

  if (loading || updatingUser) {
    return <FullscreenLoader />;
  }

  const birthDate = parseNullableValue(
    formatDate(employeeUser?.birthDate, DateFormatPatterns.shortDateWithSlash),
  );

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit}>
      {({ dirty }) => (
        <Grid container spacing={1.5}>
          <Grid size={12}>
            <DetailGridItem topText="First Name" bottomText={firstName} />
          </Grid>
          <Grid size={12}>
            <DetailGridItem topText="Date of birth" bottomText={birthDate} />
          </Grid>
          <Grid size={12}>
            <DetailGridItem topText="Last Name" bottomText={lastName} />
          </Grid>
          <Grid size={12}>
            <DetailGridItem topText="Phone Number" bottomText={formatPhoneNumber(phoneNumber)} />
          </Grid>
          <Grid size={12}>
            <DetailGridItem topText="Email Address" bottomText={email} />
          </Grid>
          {shouldRenderStatusField ? (
            <Grid size={12}>
              <Field
                name={ConsumerManageAccountActivityFormField.userStatus}
                component={SelectField}
                label="Status"
                options={STATUSES_OPTIONS}
                fullWidth
              />
            </Grid>
          ) : null}

          <Grid container spacing={3} justifyContent="center">
            <Grid>
              <Button onClick={onClose} variant="outlined" color="error">
                Close
              </Button>
            </Grid>
            <Grid>
              <Button variant="contained" loading={updating} type="submit" disabled={!dirty}>
                Save changes
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Form>
  );
};
